"use client";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectValue,
  SelectItem,
  SelectTrigger,
} from "../ui/select";
import {
  useAllAvailableTokens,
  type TokenWithMonitoring,
} from "@/features/auth/hooks/useApiToken";
import { MONITORING_BADGE_STYLES } from "@/constants/monitoring";
import { Spinner } from "../ui/spinner";

interface ApiKeyDisplayProps {
  label: string;
  value?: string;
  onCopy?: (value: string) => void;
  className?: string;
  type?: "api" | "endpoint" | "keywords";
  isLoading?: boolean;
  selectedToken?: TokenWithMonitoring | null;
  onTokenSelect?: (token: TokenWithMonitoring) => void;
  showTokenDropdown?: boolean;
  availableTokens?: TokenWithMonitoring[];
}

export default function ApiKeyDisplay({
  label,
  value,
  onCopy,
  className,
  type = "api",
  isLoading = false,
  selectedToken,
  onTokenSelect,
  showTokenDropdown = false,
  availableTokens,
}: ApiKeyDisplayProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { tokens: allTokens, isPending: tokensLoading } =
    useAllAvailableTokens();

  const tokens = availableTokens || allTokens;

  const getAlphabeticallyFirstToken = (tokenList: TokenWithMonitoring[]) => {
    if (!tokenList || tokenList.length === 0) return null;

    return tokenList.slice().sort((a, b) => a.token.localeCompare(b.token))[0];
  };

  const effectiveSelectedToken =
    selectedToken ||
    (tokens && tokens.length > 0 && !tokensLoading
      ? getAlphabeticallyFirstToken(tokens)
      : null);

  const handleCopy = () => {
    const copyValue = selectedToken?.token || value;
    if (!copyValue) return;
    if (onCopy) {
      onCopy(copyValue);
    } else {
      copyToClipboard(copyValue);
    }
    toast.success("Copied to clipboard");
  };

  // Get color based on premium status or monitoring type
  const getMonitoringColor = (token: TokenWithMonitoring) => {
    if (token.isPremium) {
      return "bg-yellow-500 text-white";
    }

    const badgeColor =
      MONITORING_BADGE_STYLES[token.monitoringType] || "bg-gray-600";
    return `${badgeColor} text-white`;
  };

  const toggleVisibility = () => setIsVisible(!isVisible);

  const displayValue = () => {
    if (type === "api" && showTokenDropdown) {
      return (
        <div className="h-full">
          <Select
            value={effectiveSelectedToken?.token_id || ""}
            onValueChange={(tokenId) => {
              const token = tokens.find(
                (t: TokenWithMonitoring) => t.token_id === tokenId,
              );
              if (token && onTokenSelect) {
                onTokenSelect(token);
              }
            }}
          >
            <SelectTrigger className="bg-brand-0 !h-[45px] w-full rounded-none border-0 px-5 text-sm font-semibold text-neutral-950/30 shadow-none">
              <SelectValue
                placeholder={tokensLoading ? "Loading..." : "Select a token..."}
              >
                {effectiveSelectedToken && (
                  <div className="flex w-full min-w-0 items-center gap-2 overflow-hidden">
                    <div
                      className={`${getMonitoringColor(effectiveSelectedToken)} flex h-7 w-7 items-center justify-center rounded-[8px] p-1`}
                    >
                      <i
                        className={`${effectiveSelectedToken.icon} text-[16px] font-light`}
                      ></i>
                    </div>

                    <span className="max-w-[80%] truncate text-xs leading-4 font-semibold -tracking-tight text-neutral-950">
                      {effectiveSelectedToken.token}...
                    </span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-brand-0 !sm:max-w-[250px] !h-auto !max-w-[90vw] !px-0">
              {tokensLoading ? (
                <SelectItem value="loading" disabled>
                  Loading tokens...
                </SelectItem>
              ) : tokens.length === 0 ? (
                <SelectItem value="no-tokens" disabled>
                  No tokens available
                </SelectItem>
              ) : (
                tokens.map((token: TokenWithMonitoring) => (
                  <SelectItem key={token.token_id} value={token.token_id}>
                    <div className="flex min-w-0 items-center gap-2">
                      <div
                        className={`${getMonitoringColor(
                          token,
                        )} flex h-7 w-7 items-center justify-center rounded-[8px] p-1`}
                      >
                        <i className={`${token.icon} text-[16px]`}></i>
                      </div>

                      <span className="block text-xs leading-4 font-semibold break-words text-neutral-950">
                        {token.token.substring(0, 21)}...
                      </span>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (type === "endpoint") {
      return isVisible ? value : "**********";
    }

    if (!value && !selectedToken) {
      return "No API Key";
    }

    return value;
  };

  const showEye =
    ((!!value && value !== "No API Token" && value !== "No API Key") ||
      !!selectedToken) &&
    !isLoading &&
    type === "endpoint";

  return (
    <div
      className={cn(
        "scrollbar-thin gap-0 overflow-hidden rounded-[8px] p-0",
        type === "keywords" ? "flex h-full flex-col" : "flex flex-col",
        className,
      )}
    >
      {/* Header */}
      <div className="flex h-[28px] w-full flex-shrink-0 items-center justify-between bg-neutral-100 px-5 py-[6px]">
        <p className="text-xs font-bold text-neutral-950">{label}</p>
        {(type === "endpoint" || type === "api") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="!p-0 text-xs font-medium text-neutral-950"
            icon
          >
            <i className="ri-file-copy-line text-[12px] text-neutral-800"></i>
            Copy
          </Button>
        )}
      </div>

      {/* Body */}
      <div
        className={cn(
          "bg-brand-0 flex items-center rounded-b-[8px] text-sm tracking-normal",
          type === "keywords" || type === "endpoint"
            ? "min-h-[45px] flex-1"
            : "min-h-[45px]",
        )}
      >
        <div
          className={cn(
            "relative flex w-full min-w-0 items-center overflow-hidden",
            type === "api" && showTokenDropdown ? "" : "px-5 py-3",
          )}
        >
          <div
            className={cn(
              "max-h-[90px] min-w-0 flex-1 overflow-auto",
              type === "api" &&
                "text-primary font-medium break-all whitespace-pre-wrap",
              type === "endpoint" &&
                "scrollbar-thin font-medium break-all whitespace-pre-wrap text-neutral-950",
              !showEye && "pr-0",
              showEye && "pr-8",
            )}
            style={{
              lineHeight: "135%",
              letterSpacing: "-2.5%",
              scrollbarWidth: "none",
              scrollbarGutter: "stable",
            }}
          >
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner size="sm" className="stroke-primary!" />
              </div>
            ) : (
              displayValue()
            )}
          </div>

          {/* Eye icon */}
          {showEye && (
            <div className="absolute top-1/2 right-4 -translate-y-1/2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleVisibility}
                className="!p-0 text-xs font-medium text-neutral-950"
                icon
              >
                {isVisible ? (
                  <i className="ri-eye-off-line text-[18px] text-neutral-800"></i>
                ) : (
                  <i className="ri-eye-line text-[18px] text-neutral-800"></i>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
